import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Card, Empty, Loading } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Building2 } from "lucide-react";
const EMPTY = { name: "", contact_person: "", phone: "", email: "", address: "", gstin: "" };

export default function Suppliers({ isOwner }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/suppliers").then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);
  const remove = async (s) => {
    if (!window.confirm(`Remove supplier ${s.name}?`)) return;
    try { await api.delete(`/suppliers/${s.id}`); toast.success("Supplier removed"); load(); } catch (e) { toast.error(apiError(e)); }
  };
  if (!rows) return <Loading />;
  return (
    <div data-testid="suppliers-tab">
      {isOwner && <div className="mb-3"><Button onClick={() => setEdit(EMPTY)} className="h-10 bg-[#0B7C5A] hover:bg-[#09664a]" data-testid="add-supplier-btn"><Plus className="w-4 h-4" /> Add supplier</Button></div>}
      {rows.length === 0 ? <Empty title="No suppliers yet" hint="Add the vendors you buy stock from to raise purchase orders." /> : (
        <>
          <Card className="hidden sm:block overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#F5F6FA] text-[#6B7189]"><tr className="text-left">
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium">Contact</th>
                <th className="px-4 py-3 font-medium">Phone</th>
                <th className="px-4 py-3 font-medium">Email</th>
                <th className="px-4 py-3 font-medium">GSTIN</th>
                {isOwner && <th className="px-4 py-3"></th>}
              </tr></thead>
              <tbody>{rows.map((s) => (
                <tr key={s.id} className="border-t border-[#E6E8F0] hover:bg-[#F5F6FA] cursor-pointer" onClick={() => navigate(`/inventory/suppliers/${s.id}`)} data-testid={`supplier-row-${s.id}`}>
                  <td className="px-4 py-3 font-medium text-[#1D4ED8]">{s.name}</td>
                  <td className="px-4 py-3 text-[#6B7189]">{s.contact_person || "—"}</td>
                  <td className="px-4 py-3 text-[#6B7189]">{s.phone || "—"}</td>
                  <td className="px-4 py-3 text-[#6B7189]">{s.email || "—"}</td>
                  <td className="px-4 py-3 text-[#6B7189] font-mono text-xs">{s.gstin || "—"}</td>
                  {isOwner && <td className="px-4 py-3 text-right"><div className="flex gap-1 justify-end">
                    <button onClick={(e) => { e.stopPropagation(); setEdit(s); }} className="h-8 w-8 rounded-lg border border-[#E6E8F0] text-[#6B7189] flex items-center justify-center" data-testid={`edit-supplier-${s.id}`}><Pencil className="w-4 h-4" /></button>
                    <button onClick={(e) => { e.stopPropagation(); remove(s); }} className="h-8 w-8 rounded-lg border border-[#E6E8F0] flex items-center justify-center" data-testid={`delete-supplier-${s.id}`}><Trash2 className="w-4 h-4 text-[#F83A84]" /></button>
                  </div></td>}
                </tr>))}</tbody>
            </table>
          </Card>
          <div className="sm:hidden space-y-2">{rows.map((s) => (
            <Card key={s.id} className="p-3 flex items-center gap-3 cursor-pointer" onClick={() => navigate(`/inventory/suppliers/${s.id}`)} data-testid={`supplier-card-${s.id}`}>
              <div className="h-9 w-9 rounded-lg bg-[#EAF3FF] text-[#1D4ED8] flex items-center justify-center shrink-0"><Building2 className="w-4 h-4" /></div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-[#101D51] truncate">{s.name}</div>
                <div className="text-[11px] text-[#6B7189] truncate">{[s.contact_person, s.phone, s.gstin].filter(Boolean).join(" · ") || "—"}</div>
              </div>
              {isOwner && <div className="flex gap-1 shrink-0">
                <button onClick={(e) => { e.stopPropagation(); setEdit(s); }} className="h-8 w-8 rounded-lg border border-[#E6E8F0] text-[#6B7189] flex items-center justify-center"><Pencil className="w-4 h-4" /></button>
                <button onClick={(e) => { e.stopPropagation(); remove(s); }} className="h-8 w-8 rounded-lg border border-[#E6E8F0] flex items-center justify-center"><Trash2 className="w-4 h-4 text-[#F83A84]" /></button>
              </div>}
            </Card>))}</div>
        </>
      )}
      {edit && <SupplierDialog supplier={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
}

function SupplierDialog({ supplier, onClose, onSaved }) {
  const isEdit = !!supplier.id;
  const [f, setF] = useState({ ...EMPTY, ...supplier });
  const [saving, setSaving] = useState(false);
  const S = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    if (!f.name.trim()) return toast.error("Supplier name is required");
    setSaving(true);
    try {
      if (isEdit) await api.put(`/suppliers/${supplier.id}`, f); else await api.post("/suppliers", f);
      toast.success(isEdit ? "Supplier updated" : "Supplier added"); onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="supplier-dialog">
        <DialogHeader><DialogTitle>{isEdit ? "Edit supplier" : "Add supplier"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Name</Label><Input className="mt-1" value={f.name} onChange={S("name")} data-testid="supplier-name" /></div>
          <div><Label>Contact person</Label><Input className="mt-1" value={f.contact_person} onChange={S("contact_person")} /></div>
          <div><Label>Phone</Label><Input className="mt-1" value={f.phone} onChange={S("phone")} data-testid="supplier-phone" /></div>
          <div><Label>Email</Label><Input className="mt-1" value={f.email} onChange={S("email")} /></div>
          <div><Label>GSTIN</Label><Input className="mt-1" value={f.gstin} onChange={S("gstin")} /></div>
          <div className="col-span-2"><Label>Address</Label><Input className="mt-1" value={f.address} onChange={S("address")} /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} disabled={saving} className="bg-[#101D51]" data-testid="supplier-save">{saving ? "Saving…" : "Save"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
